import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useLang, t, tx  } from '../context/LanguageContext';
import translations from '../i18n/translations';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';
import HeroHeadline from '../components/motion/HeroHeadline';
import SpotlightCard from '../components/motion/SpotlightCard';
import Marquee from '../components/motion/Marquee';
import CountUp from '../components/motion/CountUp';
import { RevealGroup, RevealItem } from '../components/motion/Reveal';
import { EASE } from '../lib/motion';
import { Shield, Phone, Printer, Server, Brain, GraduationCap, ArrowRight, Award, MapPin, Clock, Monitor, Wrench, Cpu, ChevronDown } from 'lucide-react';

const iconMap = { Shield, Phone, Printer, Server, Brain, GraduationCap };

// Le hero est LE moment orchestré de la page : trait, titre mot à mot, sous-titre, boutons —
// 1,2 s en tout, une seule fois. Le reste de la page ne bouge qu'en réponse au défilement ou au pointeur.
const heroStep = (delay) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: EASE, delay },
});

const HERO_IMAGE = 'https://images.unsplash.com/photo-1680992046626-418f7e910589?w=1920&q=80';

const CARD = 'block bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8 hover:border-[#0099D6]/30 transition-colors duration-300 group relative overflow-hidden h-full';
const B2C_CARD = 'block bg-[#15171E]/80 backdrop-blur-xl border border-[#0099D6]/20 rounded-2xl p-8 hover:border-[#0099D6]/50 transition-colors duration-300 group relative overflow-hidden h-full';

export default function Home() {
  const { lang } = useLang();
  const [showScroll, setShowScroll] = useState(true);
  const reduce = useReducedMotion();
  const heroRef = useRef(null);

  // Parallaxe du hero : l'image descend légèrement, le texte s'élève et s'efface quand on quitte le hero.
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', reduce ? '0%' : '18%']);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, reduce ? 1 : 0]);

  useEffect(() => {
    const onScroll = () => setShowScroll(window.scrollY < 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const hero = translations.hero;
  const pillars = translations.pillars;
  const trust = translations.trust;
  const b2c = translations.b2c;

  return (
    <>
      <SEO {...SEO_TEXTES['/']} path="/" />
    <main data-testid="home-page">
      {/* Hero */}
      {/* Marge haute = aiguillage + barre de navigation (fixes) : le contenu se centre dans l'espace restant,
          il ne passe plus sous les barres sur mobile (min-h-screen inclut ce padding, border-box). */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ paddingTop: 'calc(var(--persona-h, 72px) + 5rem)' }}
        data-testid="hero-section"
      >
        <motion.div className="absolute inset-0" style={{ y: bgY }} aria-hidden="true">
          <div
            className="kenburns absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C10] via-[#0B0C10]/90 to-[#0B0C10]/60" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 70% 50%, rgba(0,153,214,0.08), transparent 70%)' }} />
        <motion.div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20" style={{ y: contentY, opacity: contentOpacity }}>
          <div className="max-w-2xl">
            <p data-hero-reveal className="flex items-center gap-3 text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-6">
              <motion.span
                aria-hidden="true"
                className="inline-block h-px w-10 shrink-0 bg-[#0099D6] origin-left"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
              />
              <motion.span
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease: EASE, delay: 0.25 }}
              >
                MÉTÉRIS — {tx(lang, 'Infogérance & agents IA', 'Managed IT & AI agents', { es: 'Gestión IT y agentes IA', de: 'IT-Betreuung & KI-Agenten', it: 'Gestione IT e agenti IA', pt: 'Gestão IT e agentes IA' })}
              </motion.span>
            </p>
            <HeroHeadline
              key={lang}
              data-hero-reveal
              text={t(hero.slogan, lang)}
              startDelay={0.2}
              className="text-5xl md:text-7xl font-bold tracking-tight leading-tight text-white mb-6"
            />
            <motion.p {...heroStep(0.6)} data-hero-reveal className="text-lg md:text-xl leading-relaxed text-slate-300 mb-10 max-w-xl">
              {t(hero.subtitle, lang)}
            </motion.p>
            <motion.div {...heroStep(0.75)} data-hero-reveal className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="btn-shine inline-flex items-center justify-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-8 rounded-lg shadow-[0_0_30px_rgba(0,153,214,0.3)] transition-all hover:scale-105 active:scale-95 text-base"
              >
                {t(hero.cta, lang)}
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
              <a
                href="tel:0183623327"
                data-testid="hero-phone-button"
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-4 px-8 rounded-lg backdrop-blur-md transition-all text-base"
              >
                <Phone className="w-5 h-5" />
                01.83.62.33.27
              </a>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <AnimatePresence>
          {showScroll && (
            <motion.button
              key="scroll-indicator"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.4, delay: 1.4 }}
              onClick={() => window.scrollBy({ top: window.innerHeight * 0.85, behavior: 'smooth' })}
              aria-label={tx(lang, 'Défiler vers le bas', 'Scroll down', { es: 'Abajo', de: 'Nach unten blättern', it: 'Scorri verso il basso', pt: 'Descer' })}
              className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/60 hover:text-white transition-colors cursor-pointer group"
            >
              <span className="text-xs font-medium tracking-widest uppercase">
                {tx(lang, 'Défiler', 'Scroll', { es: 'Scroll', de: 'Scroll', it: 'Scambio', pt: 'Rolar' })}
              </span>
              <motion.div
                animate={reduce ? { y: 0 } : { y: [0, 6, 0] }}
                transition={{ repeat: Infinity, duration: 1.4, ease: 'easeInOut' }}
              >
                <ChevronDown className="w-6 h-6 group-hover:text-[#0099D6] transition-colors" />
              </motion.div>
            </motion.button>
          )}
        </AnimatePresence>
      </section>

      {/* Pillars / Services */}
      <section className="py-24 md:py-32 relative" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <RevealGroup className="mb-16">
            <RevealItem as="p" className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-3">
              {tx(lang, 'Nos expertises', 'Our expertise', { es: 'Nuestra experiencia', de: 'Unsere Expertise', it: 'La nostra competenza', pt: 'A nossa experiência' })}
            </RevealItem>
            <RevealItem as="h2" className="text-4xl md:text-5xl font-bold tracking-tight text-white">
              {tx(lang, 'Un écosystème IT complet, augmenté par l\'IA', 'A complete IT ecosystem, augmented by AI', { es: 'Un ecosistema IT completo, potenciado por la IA', de: 'Ein komplettes IT-Ökosystem, verstärkt durch KI', it: 'Un ecosistema IT completo, potenziato dall\'IA', pt: 'Um ecossistema informático completo, reforçado pela IA' })}
            </RevealItem>
          </RevealGroup>
          <RevealGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((pillar) => {
              const Icon = iconMap[pillar.icon];
              const cardContent = (
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-[#0099D6]/10 flex items-center justify-center mb-5 transition-all duration-300 group-hover:bg-[#0099D6]/20 group-hover:-translate-y-0.5">
                    {Icon && <Icon className="w-6 h-6 text-[#0099D6]" />}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#0099D6] transition-colors">
                    {t(pillar.title, lang)}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {t(pillar.desc, lang)}
                  </p>
                  <div className="mt-5 flex items-center gap-2 text-sm font-medium text-[#0099D6] opacity-0 -translate-x-1.5 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                    {t(translations.common.learn_more, lang)}
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              );
              const linkProps = pillar.external
                ? { as: 'a', href: pillar.link, target: '_blank', rel: 'noopener noreferrer' }
                : { as: Link, to: pillar.link };

              return (
                <RevealItem key={pillar.id} className="h-full">
                  <SpotlightCard {...linkProps} data-testid={`pillar-card-${pillar.id}`} className={CARD}>
                    {cardContent}
                  </SpotlightCard>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* Trust / Proof Section */}
      <section className="py-24 md:py-32 bg-[#15171E]/50" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <RevealGroup className="mb-16">
            <RevealItem as="p" className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-3">
              {tx(lang, 'Pourquoi MÉTÉRIS', 'Why MÉTÉRIS', { es: 'Por qué METERIS', de: 'Warum METERIS', it: 'Perché METERIS', pt: 'POR QUE METERIS' })}
            </RevealItem>
            <RevealItem as="h2" className="text-4xl md:text-5xl font-bold tracking-tight text-white">
              {t(trust.title, lang)}
            </RevealItem>
          </RevealGroup>
          <RevealGroup className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Proximity */}
            <RevealItem className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
              <MapPin className="w-8 h-8 text-[#0099D6] mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">{t(trust.proximity.title, lang)}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{t(trust.proximity.desc, lang)}</p>
            </RevealItem>
            {/* Certifications : la liste défile lentement et s'arrête au survol */}
            <RevealItem className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8 overflow-hidden">
              <Award className="w-8 h-8 text-[#0099D6] mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">{t(trust.certifications.title, lang)}</h3>
              <Marquee
                items={trust.certifications.items}
                seconds={32}
                className="mt-3 -mx-8 px-8"
                ariaLabel={t(trust.certifications.title, lang)}
                renderItem={(cert) => (
                  <span className="inline-block px-3 py-1.5 rounded-full bg-[#0099D6]/10 text-xs font-medium text-[#0099D6] border border-[#0099D6]/20 whitespace-nowrap">
                    {cert}
                  </span>
                )}
              />
            </RevealItem>
            {/* Experience */}
            <RevealItem className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
              <Clock className="w-8 h-8 text-[#0099D6] mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">{t(trust.experience.title, lang)}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{t(trust.experience.desc, lang)}</p>
              <div className="mt-4 flex items-baseline gap-2">
                <CountUp to={15} suffix="+" className="text-4xl font-bold text-[#0099D6] tabular-nums" />
                <span className="text-sm text-slate-400">{tx(lang, 'ans', 'years', { es: 'años', de: 'jahre', it: 'anni', pt: 'anos' })}</span>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* B2C Redirect Section */}
      <section className="py-24 md:py-32" data-testid="b2c-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <RevealGroup className="mb-12">
            <RevealItem as="p" className="text-sm font-medium tracking-[0.2em] uppercase text-slate-500 mb-3">
              {t(b2c.title, lang)}
            </RevealItem>
            <RevealItem as="h2" className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              {t(b2c.subtitle, lang)}
            </RevealItem>
          </RevealGroup>
          <RevealGroup className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
            <RevealItem className="h-full">
              <SpotlightCard as="a" href={b2c.repair_link} target="_blank" rel="noopener noreferrer" className={B2C_CARD}>
                <div className="relative">
                  <Wrench className="w-8 h-8 text-[#0099D6] mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#0099D6] transition-colors">{t(b2c.repair, lang)}</h3>
                  <p className="text-sm text-slate-400">{t(b2c.repair_desc, lang)}</p>
                </div>
              </SpotlightCard>
            </RevealItem>
            <RevealItem className="h-full">
              <SpotlightCard as="a" href={b2c.sales_link} target="_blank" rel="noopener noreferrer" className={B2C_CARD}>
                <div className="relative">
                  <Monitor className="w-8 h-8 text-[#0099D6] mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#0099D6] transition-colors">{t(b2c.sales, lang)}</h3>
                  <p className="text-sm text-slate-400">{t(b2c.sales_desc, lang)}</p>
                  <p className="mt-3 text-sm font-medium text-[#0099D6]">{t(b2c.sales_link_label, lang)}</p>
                </div>
              </SpotlightCard>
            </RevealItem>
            <RevealItem className="h-full">
              <SpotlightCard as={Link} to={b2c.custom_pc_link} className={B2C_CARD}>
                <div className="relative">
                  <Cpu className="w-8 h-8 text-[#0099D6] mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#0099D6] transition-colors">{t(b2c.custom_pc, lang)}</h3>
                  <p className="text-sm text-slate-400">{t(b2c.custom_pc_desc, lang)}</p>
                  <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-[#0099D6]">
                    {tx(lang, 'Demander un devis', 'Request a quote', { es: 'Solicitar una cotización', de: 'Angebot anfordern', it: 'Richiedi un preventivo', pt: 'Solicitar um orçamento' })}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </div>
              </SpotlightCard>
            </RevealItem>
          </RevealGroup>
          <RevealGroup className="mt-8">
            <RevealItem>
              <a
                href={b2c.site_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="b2c-cta"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-3 px-6 rounded-lg transition-all text-sm group"
              >
                {t(b2c.cta, lang)}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-transparent to-[#0099D6]/5" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <RevealGroup>
            <RevealItem as="h2" className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
              {tx(lang, 'Et si votre informatique vous devançait ?', 'What if your IT stayed one step ahead?', { es: '¿Y si su informática se le adelantara?', de: 'Was wäre, wenn Ihre IT Ihnen einen Schritt voraus wäre?', it: 'E se la vostra informatica vi precedesse?', pt: 'E se a sua informática se antecipasse?' })}
            </RevealItem>
            <RevealItem as="p" className="text-lg text-slate-300 mb-10 max-w-xl mx-auto">
              {tx(lang, 'Premier audit offert, sans engagement : on regarde votre parc et on vous dit ce que nos agents IA peuvent déjà prendre en charge.', 'First audit free, no commitment: we look at your estate and tell you what our AI agents can already take on.', { es: 'Primera auditoría gratuita, sin compromiso: revisamos su parque y le decimos qué pueden asumir ya nuestros agentes IA.', de: 'Erstes Audit kostenlos, unverbindlich: Wir sehen uns Ihre IT an und sagen Ihnen, was unsere KI-Agenten schon heute übernehmen können.', it: 'Primo audit gratuito, senza impegno: esaminiamo il vostro parco e vi diciamo cosa i nostri agenti IA possono già prendere in carico.', pt: 'Primeira auditoria gratuita, sem compromisso: analisamos o seu parque e dizemos-lhe o que os nossos agentes IA já podem assumir.' })}
            </RevealItem>
            <RevealItem>
              <Link
                to="/contact"
                data-testid="final-cta-button"
                className="btn-shine inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-10 rounded-lg shadow-[0_0_40px_rgba(0,153,214,0.3)] transition-all hover:scale-105 active:scale-95 text-lg"
              >
                {t(translations.common.audit_cta, lang)}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>
    </main>
    </>
  );
}
